import { Delete, Post } from "@/axios/AxiosFunctions";
import { Button } from "@/components/Core/Button";
import PopperComponent from "@/components/Core/PopperComponent";
import SearchInput from "@/components/Core/SearchInput";
import SideBarSkeleton from "@/components/Core/SideBarSkeleton";
import TableStructure from "@/components/Core/TableStructure";
import { BaseURL } from "@/config/apiUrl";
import { recordsLimit } from "@/constant/constants";
import useDebounce from "@/customHooks/useDebounce";
import { posts_table_header } from "@/data/data";
import {
  apiHeader,
  formatDateTime,
  RenderToast,
} from "@/helper/HelperFunction";
import AreYouSureModal from "@/modals/AreYouSureModal";
import ImportCSVModal from "@/modals/ImportCSVModal";
import { useEffect, useState } from "react";
import { FaTrash } from "react-icons/fa6";
import { useSelector } from "react-redux";
import classes from "./Post.module.css";

const LOADING_ACTIONS = {
  GET_POSTS: "GET_POSTS",
  SUBMIT: "SUBMIT",
};

const MODAL_STATES = {
  IMPORT_CSV: "IMPORT_CSV",
  DELETE: "DELETE",
};

const PostsPage = () => {
  const { access_token } = useSelector((state) => state?.authReducer);
  const [search, setSearch] = useState("");
  const debounce = useDebounce(search, 500);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const [modal, setModal] = useState(null);

  // get posts
  const getPosts = async (pgNo = page, _search = debounce) => {
    const url = BaseURL(
      `admin/posts/find/all?page=${pgNo}&limit=${recordsLimit}`
    );
    const params = {
      search: _search,
    };
    setLoading(LOADING_ACTIONS.GET_POSTS);
    const response = await Post(url, params, apiHeader(access_token));
    if (response) {
      setPosts(response.data.posts);
      setCount(response.data.totalCount);
    }
    setLoading(false);
  };

  useEffect(() => {
    getPosts();
  }, [debounce]);

  const handlePostAction = (action, item) => {
    if (!action || !action.value) return;
    setSelectedPost(item);
    switch (action.value) {
      case "delete":
        setModal(MODAL_STATES.DELETE);
        break;
      default:
        break;
    }
  };

  // import csv
  const handleImportCSV = async (body) => {
    const url = BaseURL(`admin/posts/create-bulk`);
    const params = {
      ...body,
    };
    setLoading(LOADING_ACTIONS.SUBMIT);
    const response = await Post(url, params, apiHeader(access_token));
    if (response) {
      RenderToast({
        message: "Posts imported successfully",
        type: "success",
      });
      setModal(null);
      setPage(1);
      getPosts(1);
    }
    setLoading(false);
  };
  // delete post
  const handleDeletePost = async () => {
    const url = BaseURL(`admin/posts/delete/${selectedPost?._id}`);
    setLoading(LOADING_ACTIONS.SUBMIT);
    const response = await Delete(url, apiHeader(access_token));
    if (response) {
      RenderToast({
        message: "Post Deleted Successfully!",
        type: "success",
      });
      setPosts((prev) =>
        prev?.filter((item) => item?._id !== selectedPost?._id)
      );
      setCount((prev) => prev - 1);
      setModal(null);
      setSelectedPost(null);
    }
    setLoading(false);
  };

  return (
    <SideBarSkeleton>
      <TableStructure
        headerTitle={
          <div className={classes.searchBox}>
            <SearchInput setter={setSearch} value={search} />
          </div>
        }
        headerHandlers={{
          button: (
            <Button
              label={"Add Bulk Post"}
              onClick={() => {
                setModal(MODAL_STATES.IMPORT_CSV);
              }}
              variant={"primary"}
            />
          ),
        }}
        tableHeaders={posts_table_header}
        tableContent={posts?.map((ele, index) => ({
          ...ele,
          type: ele?.type?.toUpperCase(),
          plant: ele?.[ele?.type]?.plant?.name,
          product: ele?.[ele?.type]?.product?.name,
          country: ele?.[ele?.type]?.country,
          capacity: ele?.[ele?.type]?.capacity,
          createdAt: formatDateTime(ele?.createdAt),
          action: (
            <PopperComponent
              data={getPopperOptions()}
              handleClick={(action) => handlePostAction(action, ele)}
            />
          ),
        }))}
        page={page}
        setPage={(e) => {
          setPage(e);
          getPosts(e);
        }}
        totalRecord={count}
        recordLimit={recordsLimit}
        isLoading={loading === LOADING_ACTIONS.GET_POSTS}
        noDataMessage={"No posts found"}
        customStyle={{
          height: "calc(100vh - 320px)",
        }}
      >
        Post
      </TableStructure>

      {modal === MODAL_STATES.IMPORT_CSV && (
        <ImportCSVModal
          show={modal === MODAL_STATES.IMPORT_CSV}
          setShow={setModal}
          handleClick={handleImportCSV}
          loading={loading === LOADING_ACTIONS.SUBMIT}
        />
      )}
      {modal === MODAL_STATES.DELETE && (
        <AreYouSureModal
          show={modal === MODAL_STATES.DELETE}
          setShow={setModal}
          onClick={handleDeletePost}
          isApiCall={loading === LOADING_ACTIONS.SUBMIT}
        />
      )}
    </SideBarSkeleton>
  );
};

export default PostsPage;

const getPopperOptions = () => {
  const options = [
    {
      icon: <FaTrash color="red" size={16} />,
      label: "Delete",
      value: "delete",
    },
  ];

  return options;
};
